/**
 * The floor plan editors' two overlay menus, the same on every platform:
 * the same rows, with the same labels, in the same order. Settings (the
 * gear) holds the measurement units and snapping. View (the eye) holds what
 * the plan shows and the 3D quality; the floor plan view's adds the room
 * labels. Each row names the preference it reads and writes
 * (`FloorPlanPreferences`), and each platform draws the rows with its own
 * widgets, reading a row with `menuCheckboxChecked` and writing one with
 * `menuCheckboxPatch` or `menuRadioPatch` into `updateFloorPlanPreferences`.
 */
import { ROOM_LABEL_MENU_LABELS, ROOM_NAME_STYLES, type RoomNameStyle } from '../assembler';
import {
  EYE_ICON,
  formatLength,
  GEAR_ICON,
  type IconPaths,
  MEASUREMENT_UNITS,
  type MeasurementUnits,
  PLAN_LAYER_CHOICES,
  type PlanLayers,
} from '../editor-model';
import type { FloorPlanPreferences } from '../preferences';
import { VIEW3D_QUALITY_CHOICES, type View3DQuality } from '../view3d';

/** A heading over the rows that follow it. */
export interface MenuHeading {
  kind: 'heading';
  label: string;
}

/** A yes-or-no row: a preference of its own, one plan layer, or (`preference` null) the walls, always shown, checked and disabled. */
export type MenuCheckbox =
  & { kind: 'checkbox'; label: string }
  & ({ preference: null } | { preference: 'snapping' | 'roomSizes' } | { preference: 'layers'; layer: keyof PlanLayers });

/** One choice of a radio group, with an example to show at its end, if any. */
export interface MenuChoice<Id extends string> {
  id: Id;
  label: string;
  example?: string;
}

/** A radio group under its heading `label`, one of `choices` on. */
export interface MenuRadioGroupOf<Preference extends keyof FloorPlanPreferences, Id extends string> {
  kind: 'radio';
  label: string;
  preference: Preference;
  choices: readonly MenuChoice<Id>[];
}

export type MenuRadioGroup =
  | MenuRadioGroupOf<'units', MeasurementUnits>
  | MenuRadioGroupOf<'roomNames', RoomNameStyle>
  | MenuRadioGroupOf<'view3dQuality', View3DQuality>;

export type FloorPlanMenuRow = MenuHeading | MenuCheckbox | MenuRadioGroup;

/** A menu: its button's label and icon, and its rows in order. */
export interface FloorPlanMenu {
  label: string;
  icon: IconPaths;
  rows: readonly FloorPlanMenuRow[];
}

/** The one length every units choice shows as its example: 10 feet 4½ inches. */
const EXAMPLE_LENGTH_FEET = 10 + 4.5 / 12;

export const SETTINGS_MENU: FloorPlanMenu = {
  label: 'Settings',
  icon: GEAR_ICON,
  rows: [
    {
      kind: 'radio',
      label: 'Measurements',
      preference: 'units',
      choices: MEASUREMENT_UNITS.map(({ id, label }) => ({ id, label, example: formatLength(EXAMPLE_LENGTH_FEET, id) })),
    },
    { kind: 'heading', label: 'Editing' },
    { kind: 'checkbox', label: 'Snap to nearby walls, openings, and edges', preference: 'snapping' },
  ],
};

/** What every plan shows: the walls always, then each layer. */
const SHOW_ROWS: readonly FloorPlanMenuRow[] = [
  { kind: 'heading', label: 'Show' },
  { kind: 'checkbox', label: 'Walls, doors, and windows', preference: null },
  ...PLAN_LAYER_CHOICES.map(([layer, label]): MenuCheckbox => ({ kind: 'checkbox', label, preference: 'layers', layer })),
];

const QUALITY_ROW: MenuRadioGroup = {
  kind: 'radio',
  label: '3D quality',
  preference: 'view3dQuality',
  choices: VIEW3D_QUALITY_CHOICES.map(([id, label]) => ({ id, label })),
};

/** The room editor's View menu. */
export const ROOM_EDITOR_VIEW_MENU: FloorPlanMenu = { label: 'View', icon: EYE_ICON, rows: [...SHOW_ROWS, QUALITY_ROW] };

/** The floor plan view's View menu: the room editor's, with each room's size and how rooms are named after the layers. */
export const FLOOR_PLAN_VIEW_MENU: FloorPlanMenu = {
  label: 'View',
  icon: EYE_ICON,
  rows: [
    ...SHOW_ROWS,
    { kind: 'checkbox', label: ROOM_LABEL_MENU_LABELS.sizes, preference: 'roomSizes' },
    { kind: 'radio', label: ROOM_LABEL_MENU_LABELS.names, preference: 'roomNames', choices: ROOM_NAME_STYLES },
    QUALITY_ROW,
  ],
};

/** Whether `row` shows checked under `preferences`; the walls always do. */
export function menuCheckboxChecked(row: MenuCheckbox, preferences: FloorPlanPreferences): boolean {
  if (row.preference === null) return true;
  return row.preference === 'layers' ? preferences.layers[row.layer] : preferences[row.preference];
}

/** The preferences that change when `row` turns `checked`; nothing for the walls. */
export function menuCheckboxPatch(row: MenuCheckbox, preferences: FloorPlanPreferences, checked: boolean): Partial<FloorPlanPreferences> {
  if (row.preference === null) return {};
  return row.preference === 'layers' ? { layers: { ...preferences.layers, [row.layer]: checked } } : { [row.preference]: checked };
}

/** The preferences that change when `row`'s choice `id` turns on. */
export const menuRadioPatch = <Row extends MenuRadioGroup>(row: Row, id: Row['choices'][number]['id']): Partial<FloorPlanPreferences> => ({
  [row.preference]: id,
});
